import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import type { User } from 'firebase/auth'
import { Capacitor } from '@capacitor/core'
import { useAuth } from './AuthContext'
import {
  getUserWorkspace,
  getMembershipState,
  publishRemoteSimulation,
  saveRemoteSnapshot,
  setCommunityUsername,
  setRemoteInteraction,
  type CommunityProfile,
} from '../lib/api'
import {
  supportedLocales,
  type ExperimentSnapshot,
  type OperationReceipt,
  type SimulationSummary,
  type SupportedLocale,
} from '../types/simulation'
import { logOperationReceipt } from '../lib/aiCostLogger'
import {
  freeMembershipProfile,
  reconcileMembershipWorkspace,
  resolveNativeMembershipProfile,
  withMembershipTimeout,
  type MembershipProfile,
} from '../lib/membership'
import { initializePlatformServices } from '../lib/platform'
import { GUEST_WORKSPACE_ADOPTED_EVENT } from '../lib/guestSession'
import {
  membershipPolicyVersion,
  isPlanAtLeast,
} from '../lib/subscriptionPolicy'
import {
  accountScopedStateKey,
  readAccountScopedState,
} from '../lib/accountScopedStorage'
import i18n from '../i18n'

interface VoxnelaState {
  credits: number
  membership: MembershipProfile
  communityProfile: CommunityProfile
  saved: string[]
  liked: string[]
  published: string[]
  followingCreators: string[]
  createdItems: SimulationSummary[]
  savedItems: SimulationSummary[]
  workspaceLoading: boolean
  membershipSyncPending: boolean
  membershipLoading: boolean
  membershipKnown: boolean
  workspaceError: boolean
  refreshWorkspace: () => Promise<void>
  snapshots: Record<string, ExperimentSnapshot>
  collectionProgress: Record<string, number>
  receipts: OperationReceipt[]
  useCredit: (idempotencyKey: string) => boolean
  settleCredit: (idempotencyKey: string) => void
  refundCredit: (idempotencyKey: string) => void
  toggleSaved: (simulationId: string) => void
  toggleLiked: (simulationId: string) => void
  publish: (simulationId: string) => Promise<void>
  updateCommunityUsername: (username: string) => Promise<CommunityProfile>
  toggleFollowingCreator: (creatorId: string) => void
  saveSnapshot: (snapshot: ExperimentSnapshot) => void
  updateCollectionProgress: (collectionId: string, index: number) => void
  addReceipt: (receipt: OperationReceipt) => void
  clearLocalUserData: () => void
}

interface PersistedState {
  membershipPolicyVersion: string
  membership?: MembershipProfile
  credits: number
  saved: string[]
  liked: string[]
  published: string[]
  followingCreators: string[]
  snapshots: Record<string, ExperimentSnapshot>
  collectionProgress: Record<string, number>
  receipts: OperationReceipt[]
  reservations: string[]
}

const initialState: PersistedState = {
  membershipPolicyVersion,
  credits: 0,
  saved: [],
  liked: [],
  published: [],
  followingCreators: [],
  snapshots: {},
  collectionProgress: {},
  receipts: [],
  reservations: [],
}

const readState = (userId: string | null): PersistedState => {
  if (!userId) return { ...initialState }
  const authenticatedInitialState: PersistedState = {
    ...initialState,
    credits: 0,
  }
  try {
    const raw = readAccountScopedState(userId)
    if (!raw) return authenticatedInitialState
    const parsed = JSON.parse(raw) as Partial<PersistedState>
    if (parsed.membershipPolicyVersion !== membershipPolicyVersion) {
      return normalizePersistedState({
        ...authenticatedInitialState,
        ...parsed,
        membershipPolicyVersion,
        credits: 0,
      })
    }
    return normalizePersistedState({ ...authenticatedInitialState, ...parsed })
  } catch {
    return authenticatedInitialState
  }
}

function cachedMembership(userId: string | null): MembershipProfile | undefined {
  const profile = readState(userId).membership
  if (!profile || !['free', 'plus', 'pro', 'studio'].includes(profile.plan)) return undefined
  if (profile.plan !== 'free' && profile.renewalAtMs && profile.renewalAtMs <= Date.now()) return undefined
  return profile
}

function normalizePersistedState(state: Partial<PersistedState>): PersistedState {
  return {
    ...initialState,
    ...state,
    saved: stringArray(state.saved),
    liked: stringArray(state.liked),
    published: stringArray(state.published),
    followingCreators: stringArray(state.followingCreators),
    snapshots: recordOrEmpty<ExperimentSnapshot>(state.snapshots),
    collectionProgress: recordOrEmpty<number>(state.collectionProgress),
    receipts: Array.isArray(state.receipts) ? state.receipts : [],
    reservations: stringArray(state.reservations),
  }
}

function stringArray(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string')
    : []
}

function recordOrEmpty<T>(value: unknown): Record<string, T> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, T>
    : {}
}

const VoxnelaContext = createContext<VoxnelaState | null>(null)

function isUnauthenticatedWorkspaceError(cause: unknown): boolean {
  const code = String((cause as { code?: string })?.code ?? '').toLowerCase()
  const message = cause instanceof Error ? cause.message.toLowerCase() : ''
  return code.includes('unauthenticated') || message.includes('unauthenticated')
}

function currentSupportedLocale(value = i18n.resolvedLanguage): SupportedLocale {
  const locale = String(value ?? '').split('-')[0] as SupportedLocale
  return supportedLocales.includes(locale) ? locale : 'en'
}

async function loadAuthenticatedWorkspace(user: User, locale: SupportedLocale) {
  await withMembershipTimeout(initializePlatformServices())
  for (let attempt = 0; attempt < 3; attempt += 1) {
    try {
      await withMembershipTimeout(user.getIdToken(attempt > 0))
      return await getUserWorkspace(locale)
    } catch (error) {
      if (!isUnauthenticatedWorkspaceError(error) || attempt === 2) throw error
    }
  }
  throw new Error('Workspace could not be loaded.')
}

async function loadAuthenticatedMembership(
  user: User,
  fallback: MembershipProfile,
  onNativeMembership: (profile: MembershipProfile, credits?: number) => void,
) {
  const nativeStore = Capacitor.isNativePlatform() && ['ios', 'android'].includes(Capacitor.getPlatform())
    ? Capacitor.getPlatform() as 'ios' | 'android' : undefined
  const native = withMembershipTimeout(resolveNativeMembershipProfile(user.uid, fallback), 8_000)
    .then((profile) => { if (profile.plan !== 'free') onNativeMembership(profile); return profile })
    .catch((error) => { console.warn('[VOXNELA_MEMBERSHIP]', error); return fallback })
  const server = (async () => {
    await withMembershipTimeout(initializePlatformServices())
    await withMembershipTimeout(user.getIdToken())
    const result = await getMembershipState(nativeStore)
    if (result.membership.plan !== 'free') onNativeMembership(result.membership, result.credits)
    return result
  })()
  const [nativeResult, serverResult] = await Promise.allSettled([native, server])
  if (serverResult.status === 'rejected') throw serverResult.reason
  const nativeProfile = nativeResult.status === 'fulfilled' ? nativeResult.value : fallback
  let result = reconcileMembershipWorkspace(serverResult.value, nativeProfile)
  if (nativeStore && result.membershipSyncPending) {
    result = reconcileMembershipWorkspace(await getMembershipState(nativeStore, true), nativeProfile)
  }
  return result
}

export function VoxnelaProvider({ children }: PropsWithChildren) {
  const { user } = useAuth()
  const userId = user?.uid ?? null
  const [state, setState] = useState<PersistedState>(() => readState(userId))
  const [membership, setMembership] = useState<MembershipProfile>(
    () => cachedMembership(userId) ?? freeMembershipProfile(user?.metadata.creationTime),
  )
  const [communityProfile, setCommunityProfile] = useState<CommunityProfile>({
    username: null,
    handle: null,
    usernameSet: false,
    usernameSetAtMs: 0,
  })
  const [createdItems, setCreatedItems] = useState<SimulationSummary[]>([])
  const [savedItems, setSavedItems] = useState<SimulationSummary[]>([])
  const [workspaceLoading, setWorkspaceLoading] = useState(false)
  const [workspaceUserId, setWorkspaceUserId] = useState<string | null>(cachedMembership(userId) ? userId : null)
  const [membershipKnown, setMembershipKnown] = useState(Boolean(cachedMembership(userId)))
  const [workspaceError, setWorkspaceError] = useState(false)
  const membershipRequestRef = useRef<{ userId: string; promise: Promise<void> } | null>(null)
  const activeUserRef = useRef(userId)
  const [membershipSyncPending, setMembershipSyncPending] = useState(false)
  const [workspaceLocale, setWorkspaceLocale] = useState<SupportedLocale>(
    () => currentSupportedLocale(),
  )
  const snapshotSaveTimers = useRef(new Map<string, number>())

  useEffect(() => {
    const handleLanguageChanged = (locale: string) => {
      setWorkspaceLocale(currentSupportedLocale(locale))
    }
    i18n.on('languageChanged', handleLanguageChanged)
    return () => {
      i18n.off('languageChanged', handleLanguageChanged)
    }
  }, [])

  const changeState = useCallback(
    (updater: (current: PersistedState) => PersistedState) => {
      setState((current) => {
        const next = updater(current)
        localStorage.setItem(accountScopedStateKey(userId), JSON.stringify(next))
        return next
      })
    },
    [userId],
  )

  useEffect(() => {
    activeUserRef.current = userId
    let active = true
    queueMicrotask(() => {
      if (!active) return
      setState(readState(userId))
      setWorkspaceUserId(cachedMembership(userId) ? userId : null)
      setMembershipKnown(Boolean(cachedMembership(userId)))
      setWorkspaceError(false)
      setMembershipSyncPending(false)
      setCreatedItems([])
      setSavedItems([])
      setCommunityProfile({
        username: null,
        handle: null,
        usernameSet: false,
        usernameSetAtMs: 0,
      })
    })
    return () => {
      active = false
      if (activeUserRef.current === userId) activeUserRef.current = null
    }
  }, [userId])

  useEffect(() => {
    if (!user) {
      let active = true
      queueMicrotask(() => {
        if (active) {
          setMembership(freeMembershipProfile())
          setMembershipSyncPending(false)
          setCommunityProfile({
            username: null,
            handle: null,
            usernameSet: false,
            usernameSetAtMs: 0,
          })
        }
      })
      return () => {
        active = false
      }
    }
    let active = true
    queueMicrotask(() => {
      if (active) setMembership(cachedMembership(user.uid) ?? freeMembershipProfile(user.metadata.creationTime))
    })
    queueMicrotask(() => { if (active) setWorkspaceLoading(true) })
    void loadAuthenticatedWorkspace(user, workspaceLocale)
      .then((workspace) => {
        if (!active) return
        setCreatedItems(workspace.created)
        setSavedItems(workspace.saved)
        setCommunityProfile(workspace.profile)
        changeState((current) => ({
          ...current,
          saved: Array.from(new Set([...current.saved.filter((id) => id.startsWith('pilot-')), ...workspace.savedIds])),
          liked: Array.from(new Set([...current.liked.filter((id) => id.startsWith('pilot-')), ...workspace.likedIds])),
          followingCreators: workspace.followingCreatorIds.filter(
            (creatorId) => creatorId !== user.uid,
          ),
          snapshots: {
            ...current.snapshots,
            ...Object.fromEntries(
              workspace.snapshots.map((snapshot) => [
                snapshot.simulationId,
                snapshot,
              ]),
            ),
          },
        }))
      })
      .catch((error) => console.error('[VOXNELA_WORKSPACE]', error))
      .finally(() => { if (active) setWorkspaceLoading(false) })
    return () => { active = false }
  }, [changeState, user, workspaceLocale])

  const refreshMembership = useCallback(async () => {
    if (!user) return
    if (membershipRequestRef.current?.userId === user.uid) return membershipRequestRef.current.promise
    const cached = cachedMembership(user.uid)
    const fallback = cached ?? freeMembershipProfile(user.metadata.creationTime)
    let displayedProfile = fallback
    const request = (async () => {
      try {
        const result = await loadAuthenticatedMembership(user, fallback, (profile, credits) => {
          if (activeUserRef.current !== user.uid) return
          if (!isPlanAtLeast(profile.plan, displayedProfile.plan)) return
          const planChanged = profile.plan !== displayedProfile.plan
          displayedProfile = profile
          setMembership(profile)
          setMembershipKnown(true)
          if (planChanged && credits === undefined) setMembershipSyncPending(true)
          if (credits !== undefined) setMembershipSyncPending(false)
          changeState((current) => ({ ...current, membership: profile, ...(credits !== undefined ? { credits } : {}) }))
          if (credits !== undefined) setWorkspaceUserId(user.uid)
        })
        if (activeUserRef.current !== user.uid) return
        setMembership(result.membership)
        setMembershipKnown(true)
        setWorkspaceUserId(user.uid)
        setMembershipSyncPending(result.membershipSyncPending)
        setWorkspaceError(result.membershipSyncPending)
        changeState((current) => ({ ...current, membership: result.membership, credits: result.credits }))
        console.info('[VOXNELA_MEMBERSHIP_READY]', result.membership.plan, result.credits, result.membershipSyncPending)
      } catch (error) {
        if (activeUserRef.current === user.uid) setWorkspaceError(true)
        console.warn('[VOXNELA_MEMBERSHIP_STATE]', error)
        throw error
      } finally {
        if (membershipRequestRef.current?.userId === user.uid) membershipRequestRef.current = null
      }
    })()
    membershipRequestRef.current = { userId: user.uid, promise: request }
    return request
  }, [changeState, user])

  useEffect(() => {
    if (!user) return
    void refreshMembership().catch(() => {})
  }, [refreshMembership, user])

  const refreshWorkspace = useCallback(async () => {
    if (!user) return
    await refreshMembership()
    // Gallery and legacy workspace queries must not block membership or purchases.
    void loadAuthenticatedWorkspace(user, workspaceLocale).then((workspace) => {
      if (activeUserRef.current !== user.uid) return
      setCreatedItems(workspace.created)
      setSavedItems(workspace.saved)
      setCommunityProfile(workspace.profile)
    }).catch((error) => console.warn('[VOXNELA_WORKSPACE]', error))
  }, [refreshMembership, user, workspaceLocale])

  useEffect(() => {
    if (!user) return
    let refreshing = false
    const refreshWhenVisible = () => {
      if (document.visibilityState !== 'visible' || refreshing) return
      refreshing = true
      void refreshWorkspace()
        .catch((error) => console.warn('[VOXNELA_CREDITS_SYNC]', error))
        .finally(() => { refreshing = false })
    }
    document.addEventListener('visibilitychange', refreshWhenVisible)
    window.addEventListener('focus', refreshWhenVisible)
    return () => {
      document.removeEventListener('visibilitychange', refreshWhenVisible)
      window.removeEventListener('focus', refreshWhenVisible)
    }
  }, [refreshWorkspace, user])

  // Credits and a restored subscription change once a guest's work is moved
  // into the account that was just signed in to.
  useEffect(() => {
    if (!user) return
    const refreshAfterAdoption = () => {
      void refreshWorkspace().catch((error) => console.warn('[VOXNELA_GUEST_ADOPTION]', error))
    }
    window.addEventListener(GUEST_WORKSPACE_ADOPTED_EVENT, refreshAfterAdoption)
    return () => window.removeEventListener(GUEST_WORKSPACE_ADOPTED_EVENT, refreshAfterAdoption)
  }, [refreshWorkspace, user])

  const useCredit = useCallback(
    (idempotencyKey: string) => {
      if (!user || membershipSyncPending || workspaceError || workspaceUserId !== user.uid) return false
      if (state.reservations.includes(idempotencyKey)) return true
      if (state.credits < 1) return false
      changeState((current) => ({
        ...current,
        credits: current.credits - 1,
        reservations: [...current.reservations, idempotencyKey],
      }))
      return true
    },
    [changeState, state.credits, state.reservations, user, membershipSyncPending, workspaceError, workspaceUserId],
  )

  const refundCredit = useCallback(
    (idempotencyKey: string) => {
      if (!user) return
      changeState((current) => {
        if (!current.reservations.includes(idempotencyKey)) return current
        return {
          ...current,
          credits: current.credits + 1,
          reservations: current.reservations.filter(
            (reservation) => reservation !== idempotencyKey,
          ),
        }
      })
    },
    [changeState, user],
  )

  const settleCredit = useCallback(
    (idempotencyKey: string) => {
      if (!user) return
      changeState((current) => ({
        ...current,
        reservations: current.reservations.filter((reservation) => reservation !== idempotencyKey),
      }))
    },
    [changeState, user],
  )

  const toggleSaved = useCallback(
    (simulationId: string) => {
      const enabled = !state.saved.includes(simulationId)
      changeState((current) => ({
        ...current,
        saved: current.saved.includes(simulationId)
          ? current.saved.filter((id) => id !== simulationId)
          : [...current.saved, simulationId],
      }))
      if (user && !simulationId.startsWith('pilot-')) {
        void setRemoteInteraction('save', simulationId, enabled)
          .then(() => getUserWorkspace(workspaceLocale))
          .then((workspace) => setSavedItems(workspace.saved))
          .catch((error) => console.error('[VOXNELA_SAVE]', error))
      }
    },
    [changeState, state.saved, user, workspaceLocale],
  )

  const toggleLiked = useCallback(
    (simulationId: string) => {
      const enabled = !state.liked.includes(simulationId)
      changeState((current) => ({
        ...current,
        liked: current.liked.includes(simulationId)
          ? current.liked.filter((id) => id !== simulationId)
          : [...current.liked, simulationId],
      }))
      if (user && !simulationId.startsWith('pilot-')) {
        void setRemoteInteraction('like', simulationId, enabled)
          .catch((error) => console.error('[VOXNELA_LIKE]', error))
      }
    },
    [changeState, state.liked, user],
  )

  const publish = useCallback(
    async (simulationId: string) => {
      if (!user) throw new Error('Yayınlamak için giriş yapmalısın.')
      await publishRemoteSimulation(simulationId)
      changeState((current) => ({
        ...current,
        published: current.published.includes(simulationId)
          ? current.published
          : [...current.published, simulationId],
      }))
      const workspace = await getUserWorkspace(workspaceLocale)
      setCreatedItems(workspace.created)
      setCommunityProfile(workspace.profile)
    },
    [changeState, user, workspaceLocale],
  )

  const updateCommunityUsername = useCallback(
    async (username: string) => {
      if (!user) {
        throw new Error('Kullanıcı adı belirlemek için giriş yapmalısın.')
      }
      const result = await setCommunityUsername(username)
      const profile: CommunityProfile = {
        username: result.username,
        handle: result.handle,
        usernameSet: result.usernameSet,
        usernameSetAtMs:
          communityProfile.usernameSetAtMs || Date.now(),
      }
      setCommunityProfile(profile)
      setCreatedItems((items) =>
        items.map((item) => ({
          ...item,
          creator: {
            ...item.creator,
            handle: result.handle,
            username: result.username,
          },
        })),
      )
      return profile
    },
    [communityProfile.usernameSetAtMs, user],
  )

  const toggleFollowingCreator = useCallback(
    (creatorId: string) => {
      if (!creatorId || creatorId === user?.uid) return
      const enabled = !state.followingCreators.includes(creatorId)
      changeState((current) => ({
        ...current,
        followingCreators: current.followingCreators.includes(creatorId)
          ? current.followingCreators.filter((id) => id !== creatorId)
          : [...current.followingCreators, creatorId],
      }))
      if (user) {
        void setRemoteInteraction('follow', creatorId, enabled)
          .catch((error) => console.error('[VOXNELA_FOLLOW]', error))
      }
    },
    [changeState, state.followingCreators, user],
  )

  const saveSnapshot = useCallback(
    (snapshot: ExperimentSnapshot) => {
      changeState((current) => ({
        ...current,
        snapshots: {
          ...current.snapshots,
          [snapshot.simulationId]: snapshot,
        },
      }))
      if (user && !snapshot.simulationId.startsWith('pilot-')) {
        const previousTimer = snapshotSaveTimers.current.get(
          snapshot.simulationId,
        )
        if (previousTimer !== undefined) window.clearTimeout(previousTimer)
        const timer = window.setTimeout(() => {
          snapshotSaveTimers.current.delete(snapshot.simulationId)
          void saveRemoteSnapshot(snapshot)
            .catch((error) => console.error('[VOXNELA_SNAPSHOT]', error))
        }, 450)
        snapshotSaveTimers.current.set(snapshot.simulationId, timer)
      }
    },
    [changeState, user],
  )

  useEffect(() => () => {
    snapshotSaveTimers.current.forEach((timer) => window.clearTimeout(timer))
    snapshotSaveTimers.current.clear()
  }, [])

  const updateCollectionProgress = useCallback(
    (collectionId: string, index: number) => {
      changeState((current) => ({
        ...current,
        collectionProgress: {
          ...current.collectionProgress,
          [collectionId]: index,
        },
      }))
    },
    [changeState],
  )

  const addReceipt = useCallback(
    (receipt: OperationReceipt) => {
      logOperationReceipt(receipt)
      changeState((current) => ({
        ...current,
        receipts: [receipt, ...current.receipts].slice(0, 100),
      }))
    },
    [changeState],
  )

  const clearLocalUserData = useCallback(() => {
    for (const key of Object.keys(localStorage)) {
      if (key.startsWith('voxnela:')) localStorage.removeItem(key)
    }
    for (const key of Object.keys(sessionStorage)) {
      if (key.startsWith('voxnela:')) sessionStorage.removeItem(key)
    }
    setState({ ...initialState })
    setCreatedItems([])
    setSavedItems([])
    setCommunityProfile({
      username: null,
      handle: null,
      usernameSet: false,
      usernameSetAtMs: 0,
    })
  }, [])

  const value = useMemo<VoxnelaState>(
    () => ({
      ...state,
      credits: user ? state.credits : 0,
      membership,
      communityProfile,
      createdItems: user ? createdItems : [],
      savedItems: user ? savedItems : [],
      workspaceLoading,
      membershipLoading: Boolean(user && !membershipKnown && !workspaceError),
      workspaceError,
      membershipKnown,
      membershipSyncPending,
      refreshWorkspace,
      useCredit,
      settleCredit,
      refundCredit,
      toggleSaved,
      toggleLiked,
      publish,
      updateCommunityUsername,
      toggleFollowingCreator,
      saveSnapshot,
      updateCollectionProgress,
      addReceipt,
      clearLocalUserData,
    }),
    [
      state,
      membership,
      communityProfile,
      createdItems,
      savedItems,
      workspaceLoading,
      membershipKnown,
      workspaceError,
      membershipSyncPending,
      refreshWorkspace,
      user,
      useCredit,
      settleCredit,
      refundCredit,
      toggleSaved,
      toggleLiked,
      publish,
      updateCommunityUsername,
      toggleFollowingCreator,
      saveSnapshot,
      updateCollectionProgress,
      addReceipt,
      clearLocalUserData,
    ],
  )

  return (
    <VoxnelaContext.Provider value={value}>
      {children}
    </VoxnelaContext.Provider>
  )
}

export const useVoxnela = () => {
  const value = useContext(VoxnelaContext)
  if (!value) throw new Error('useVoxnela must be used inside VoxnelaProvider')
  return value
}
